import { useState } from 'react';
import { apiWrite } from '../../lib/api-client.js';
import type { SafetyMeeting } from './use-safety-read-model.js';

interface OpenMeetingResponse {
  meeting?: SafetyMeeting;
  error?: string;
}

interface CloseMeetingResponse {
  closed?: boolean;
  closed_by?: string;
  closed_at?: string;
  duration_minutes?: number;
  signature_count?: number;
  close_mode?: string;
  error?: string;
}

interface SignMeetingResponse {
  name?: string;
  signed_at?: string;
  error?: string;
}

export function useSafetyWrite(onSuccess: (meeting?: SafetyMeeting) => void) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function openMeeting(terminalNumber: string, topic: string) {
    setSubmitting(true);
    setError(null);

    try {
      const { ok, data } = await apiWrite<OpenMeetingResponse>(
        '/api/safety-meeting/open',
        'POST',
        { terminal_number: terminalNumber, topic },
      );

      if (!ok || data.error) {
        setError(data.error || 'Failed to open safety meeting');
        return false;
      }

      onSuccess(data.meeting);
      return data.meeting || true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Normal manager close for the kiosk/operator flow. The worker validates
   * manager auth + safety permission off the bearer token.
   */
  async function closeMeeting(meetingId: string | number) {
    setSubmitting(true);
    setError(null);

    try {
      const { ok, data } = await apiWrite<CloseMeetingResponse>(
        `/api/safety-meeting/${meetingId}/close`,
        'POST',
        {},
      );

      if (!ok || data.error) {
        setError(data.error || 'Failed to close safety meeting');
        return false;
      }

      onSuccess();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function forceCloseMeeting(meetingId: string | number, closeCode: string, driverId: string) {
    setSubmitting(true);
    setError(null);

    try {
      const { ok, data } = await apiWrite<CloseMeetingResponse>(
        `/api/safety-meeting/${meetingId}/close`,
        'POST',
        { close_code: closeCode, driver_id: driverId },
      );

      if (!ok || data.error) {
        setError(data.error || 'Failed to close safety meeting');
        return false;
      }

      onSuccess();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function signMeeting(meetingId: string | number, driverId: string, signatureData: string) {
    setSubmitting(true);
    setError(null);

    try {
      const { ok, data } = await apiWrite<SignMeetingResponse>(
        `/api/safety-meeting/${meetingId}/sign`,
        'POST',
        { driver_id: driverId, signature_data: signatureData },
      );

      if (!ok || data.error) {
        setError(data.error || 'Failed to record signature');
        return false;
      }

      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    openMeeting,
    closeMeeting,
    forceCloseMeeting,
    signMeeting,
    submitting,
    error,
    clearError: () => setError(null),
  };
}
